import type {
  Promotion,
  PromotionAutoPayConfiguration,
  PromotionBenefit,
  PromotionCode,
  PromotionCondition,
  PromotionEligibilityScope,
} from '@/lib/commerce';
import { formatMinorCurrencyInput, parseCurrencyInputToMinor } from './currency';

export type DiscountType = 'standard' | 'conditional' | 'context' | 'code_required';
export type RedemptionMethod = 'automatic' | 'customer_code' | 'hidden_code';

export type AutoPayConfigurationDraft = {
  label: string;
  description: string;
  required: boolean;
  trueLabel: string;
  falseLabel: string;
  disclosure: string;
  enrollmentStatus: 'pending';
  deferredEnrollmentCopy: string;
};

export type EligibilityScopeDraft = {
  scopeType: string;
  scopeValue: string;
  exclude: boolean;
  metadata: Record<string, unknown>;
};

export type DiscountFormState = {
  promotionKey: string;
  name: string;
  description: string;
  status: string;
  discountType: DiscountType;
  redemptionMethod: RedemptionMethod;
  startsAt: string;
  endsAt: string;
  stackPolicy: string;
  stackGroup: string;
  priority: string;
  benefitType: string;
  appliesTo: string;
  currency: string;
  amount: string;
  percentage: string;
  maxDiscount: string;
  discountLength: string;
  applicationPolicy: string;
  conditionType: string;
  conditionValue: string;
  preservedConditions: PromotionCondition[];
  eligibilityScopes: EligibilityScopeDraft[];
  autoPayConfiguration: AutoPayConfigurationDraft;
  code: string;
  codeStatus: string;
  codeUseLimit: string;
  codeStartsAt: string;
  codeEndsAt: string;
  displayJSON: string;
  metadataJSON: string;
};

export const DISCOUNT_TYPE_OPTIONS: Array<{ value: DiscountType; label: string; description: string }> = [
  { value: 'standard', label: 'Standard', description: 'Applies automatically when eligibility matches.' },
  { value: 'conditional', label: 'Conditional', description: 'Requires a customer choice or state, such as AutoPay.' },
  { value: 'context', label: 'Context', description: 'Requires trusted cart or request context, such as an agent signup.' },
  { value: 'code_required', label: 'Code Required', description: 'Requires a valid customer-entered redemption code.' },
];

export const CONDITION_TYPE_OPTIONS = [
  { value: 'autopay_enabled', label: 'AutoPay enabled', discountType: 'conditional' as const },
  { value: 'signup_experience', label: 'Signup experience', discountType: 'context' as const },
  { value: 'source_channel', label: 'Source channel', discountType: 'context' as const },
  { value: 'agent_user', label: 'Agent user', discountType: 'context' as const },
];

export const DEFAULT_AUTOPAY_CONFIGURATION: AutoPayConfigurationDraft = {
  label: 'AutoPay',
  description: 'Save when you set up automatic payments.',
  required: true,
  trueLabel: 'Use AutoPay',
  falseLabel: 'Standard billing',
  disclosure: 'Payment method setup follows order confirmation.',
  enrollmentStatus: 'pending',
  deferredEnrollmentCopy: 'Secure AutoPay setup follows order and account confirmation.',
};

export const PROMOTION_START_DEFAULT_TIME = '00:00';
export const PROMOTION_END_DEFAULT_TIME = '23:59';

export function scheduleDatePart(value: string): string {
  const date = value.split('T', 1)[0] ?? '';
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : '';
}

export function scheduleTimePart(value: string): string {
  const time = value.includes('T') ? value.split('T', 2)[1]?.slice(0, 5) ?? '' : '';
  return /^\d{2}:\d{2}$/.test(time) ? time : '';
}

export function updateScheduleDate(currentValue: string, date: string, defaultTime: string): string {
  const normalizedDate = date.trim();
  if (!normalizedDate) return '';
  return `${normalizedDate}T${scheduleTimePart(currentValue) || defaultTime}`;
}

export function updateScheduleTime(currentValue: string, time: string, defaultTime: string): string {
  const date = scheduleDatePart(currentValue);
  if (!date) return currentValue;
  return `${date}T${time.trim() || defaultTime}`;
}

export function createEmptyEligibilityScope(): EligibilityScopeDraft {
  return { scopeType: 'catalog', scopeValue: '', exclude: false, metadata: {} };
}

function formatJSONObject(value: Record<string, unknown> | undefined): string {
  return JSON.stringify(value ?? {}, null, 2);
}

function parseJSONObject(value: string, label: string): Record<string, unknown> {
  if (!value.trim()) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    throw new Error(`${label} must be valid JSON.`);
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error(`${label} must be a JSON object.`);
  }
  return parsed as Record<string, unknown>;
}

function toDatetimeLocal(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function toISOString(value: string, label: string): string | undefined {
  if (!value.trim()) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) throw new Error(`${label} is invalid.`);
  return date.toISOString();
}

function parseOptionalInteger(value: string, label: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed)) throw new Error(`${label} must be a whole number.`);
  return parsed;
}

function parseOptionalFloat(value: string, label: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number.parseFloat(value);
  if (Number.isNaN(parsed)) throw new Error(`${label} must be a number.`);
  return parsed;
}

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function autoPayConfigurationFromPromotion(promotion: Promotion): AutoPayConfigurationDraft {
  const configured = promotion.autopay_configuration;

  return {
    label: configured?.label ?? DEFAULT_AUTOPAY_CONFIGURATION.label,
    description: configured?.description ?? DEFAULT_AUTOPAY_CONFIGURATION.description,
    required: configured?.required ?? DEFAULT_AUTOPAY_CONFIGURATION.required,
    trueLabel: configured?.true_label ?? DEFAULT_AUTOPAY_CONFIGURATION.trueLabel,
    falseLabel: configured?.false_label ?? DEFAULT_AUTOPAY_CONFIGURATION.falseLabel,
    disclosure: configured?.disclosure ?? DEFAULT_AUTOPAY_CONFIGURATION.disclosure,
    enrollmentStatus: 'pending',
    deferredEnrollmentCopy: configured?.deferred_enrollment_copy
      ?? DEFAULT_AUTOPAY_CONFIGURATION.deferredEnrollmentCopy,
  };
}

function conditionValueForForm(condition: PromotionCondition | undefined): string {
  if (condition?.value == null || typeof condition.value === 'object') return '';
  return String(condition.value);
}

function editableCondition(condition: PromotionCondition): PromotionCondition {
  return {
    condition_type: condition.condition_type,
    operator: condition.operator,
    value: condition.value,
    metadata: asObject(condition.metadata),
  };
}

export function createEmptyPromotionForm(): DiscountFormState {
  return {
    promotionKey: '',
    name: '',
    description: '',
    status: 'draft',
    discountType: 'standard',
    redemptionMethod: 'automatic',
    startsAt: '',
    endsAt: '',
    stackPolicy: 'combinable',
    stackGroup: '',
    priority: '100',
    benefitType: 'percent_off',
    appliesTo: 'order',
    currency: 'USD',
    amount: '',
    percentage: '',
    maxDiscount: '',
    discountLength: '',
    applicationPolicy: 'overlap',
    conditionType: '',
    conditionValue: '',
    preservedConditions: [],
    eligibilityScopes: [],
    autoPayConfiguration: { ...DEFAULT_AUTOPAY_CONFIGURATION },
    code: '',
    codeStatus: 'active',
    codeUseLimit: '',
    codeStartsAt: '',
    codeEndsAt: '',
    displayJSON: formatJSONObject({}),
    metadataJSON: formatJSONObject({}),
  };
}

export function selectDiscountType(form: DiscountFormState, discountType: DiscountType): DiscountFormState {
  if (discountType === 'conditional') {
    return {
      ...form,
      discountType,
      redemptionMethod: 'automatic',
      conditionType: 'autopay_enabled',
      conditionValue: 'true',
      autoPayConfiguration: { ...DEFAULT_AUTOPAY_CONFIGURATION, ...form.autoPayConfiguration },
    };
  }
  if (discountType === 'context') {
    return {
      ...form,
      discountType,
      redemptionMethod: 'automatic',
      conditionType: form.conditionType === 'autopay_enabled' ? 'signup_experience' : (form.conditionType || 'signup_experience'),
      conditionValue: form.conditionType === 'autopay_enabled' ? 'agent' : form.conditionValue,
    };
  }
  if (discountType === 'code_required') {
    return { ...form, discountType, redemptionMethod: 'customer_code', conditionType: '', conditionValue: '' };
  }
  return { ...form, discountType, redemptionMethod: 'automatic', conditionType: '', conditionValue: '' };
}

export function buildPromotionForm(promotion: Promotion): DiscountFormState {
  const form = createEmptyPromotionForm();
  const primaryBenefit = promotion.benefits?.[0];
  const conditions = promotion.conditions ?? [];
  const discountType = promotion.discount_type;
  const expectedCondition = discountType === 'conditional'
    ? conditions.find(condition => condition.condition_type === 'autopay_enabled')
    : discountType === 'context'
      ? conditions[0]
      : undefined;
  const scopes = promotion.eligibility_scopes ?? [];
  const primaryCode = promotion.codes?.[0];

  return {
    ...form,
    promotionKey: promotion.promotion_key || '',
    name: promotion.name,
    description: promotion.description || '',
    status: promotion.status,
    discountType,
    redemptionMethod: promotion.redemption_method,
    startsAt: toDatetimeLocal(promotion.starts_at),
    endsAt: toDatetimeLocal(promotion.ends_at),
    stackPolicy: promotion.stack_policy || 'combinable',
    stackGroup: promotion.stack_group || '',
    priority: `${promotion.priority ?? 100}`,
    benefitType: primaryBenefit?.benefit_type || 'percent_off',
    appliesTo: primaryBenefit?.applies_to || 'order',
    currency: primaryBenefit?.currency || 'USD',
    amount: formatMinorCurrencyInput(primaryBenefit?.amount_minor, primaryBenefit?.currency || 'USD'),
    percentage: primaryBenefit?.percentage != null ? `${primaryBenefit.percentage}` : '',
    maxDiscount: formatMinorCurrencyInput(
      primaryBenefit?.max_discount_minor,
      primaryBenefit?.currency || 'USD',
    ),
    discountLength: primaryBenefit?.discount_length ? `${primaryBenefit.discount_length}` : '',
    applicationPolicy: primaryBenefit?.application_policy ?? 'overlap',
    conditionType: expectedCondition?.condition_type || '',
    conditionValue: discountType === 'conditional' ? 'true' : conditionValueForForm(expectedCondition),
    preservedConditions: conditions
      .filter(condition => condition !== expectedCondition)
      .map(editableCondition),
    eligibilityScopes: scopes.map(scope => ({
      scopeType: scope.scope_type,
      scopeValue: scope.scope_value || '',
      exclude: Boolean(scope.exclude),
      metadata: asObject(scope.metadata),
    })),
    autoPayConfiguration: autoPayConfigurationFromPromotion(promotion),
    code: primaryCode?.code || '',
    codeStatus: primaryCode?.status || 'active',
    codeUseLimit: primaryCode?.use_limit != null ? `${primaryCode.use_limit}` : '',
    codeStartsAt: toDatetimeLocal(primaryCode?.starts_at),
    codeEndsAt: toDatetimeLocal(primaryCode?.ends_at),
    displayJSON: formatJSONObject(promotion.display),
    metadataJSON: formatJSONObject(promotion.metadata),
  };
}

function validateAutoPayConfiguration(draft: AutoPayConfigurationDraft): void {
  if (!draft.label.trim()) throw new Error('AutoPay label is required.');
  if (!draft.description.trim()) throw new Error('AutoPay description is required.');
  if (!draft.trueLabel.trim() || !draft.falseLabel.trim()) {
    throw new Error('Both AutoPay option labels are required.');
  }
  if (draft.trueLabel.trim().toLowerCase() === draft.falseLabel.trim().toLowerCase()) {
    throw new Error('AutoPay option labels must be different.');
  }
  if (!draft.disclosure.trim() && !draft.deferredEnrollmentCopy.trim()) {
    throw new Error('AutoPay disclosure or deferred-enrollment copy is required.');
  }
}

function buildBenefit(form: DiscountFormState): PromotionBenefit {
  const benefit: PromotionBenefit = {
    benefit_type: form.benefitType,
    applies_to: form.appliesTo.trim() || 'order',
    metadata: {},
  };
  benefit.application_policy = form.benefitType === 'free_periods' ? form.applicationPolicy : 'overlap';
  if (form.benefitType !== 'free_periods') {
    const maxDiscountMinor = parseCurrencyInputToMinor(form.maxDiscount, form.currency, 'Max discount');
    if (maxDiscountMinor !== undefined) benefit.max_discount_minor = maxDiscountMinor;
  }
  benefit.discount_length = parseOptionalInteger(form.discountLength, 'Discount length') ?? 0;
  if (form.benefitType === 'free_periods') {
    if (benefit.discount_length <= 0) throw new Error('Free periods require a positive number of billing cycles.');
    if (!['order', 'line_item'].includes(benefit.applies_to)) throw new Error('Free periods apply only to recurring order or line item charges.');
    if (!['overlap', 'extend_free_periods'].includes(form.applicationPolicy)) throw new Error('Choose a valid free-period application policy.');
  }
  if (form.benefitType === 'amount_off' || form.benefitType === 'fixed_price') {
    const amountMinor = parseCurrencyInputToMinor(form.amount, form.currency, 'Amount');
    if (amountMinor === undefined) throw new Error('Amount is required for this benefit type.');
    benefit.amount_minor = amountMinor;
    benefit.currency = form.currency.trim() || 'USD';
  }
  if (form.benefitType === 'percent_off') {
    const percentage = parseOptionalFloat(form.percentage, 'Percentage');
    if (percentage === undefined) throw new Error('Percentage is required for percent-off benefits.');
    benefit.percentage = percentage;
  }
  return benefit;
}

function buildConditions(form: DiscountFormState): PromotionCondition[] {
  if (form.discountType === 'conditional') {
    if (form.conditionType !== 'autopay_enabled') throw new Error('Conditional discounts require a supported condition.');
    validateAutoPayConfiguration(form.autoPayConfiguration);
    return [
      { condition_type: 'autopay_enabled', operator: 'eq', value: true, metadata: {} },
      ...form.preservedConditions.map(editableCondition),
    ];
  }
  if (form.discountType === 'context') {
    if (!['signup_experience', 'source_channel', 'agent_user'].includes(form.conditionType)) {
      throw new Error('Context discounts require a supported context condition.');
    }
    if (!form.conditionValue.trim()) throw new Error('Context condition value is required.');
    return [{
      condition_type: form.conditionType,
      operator: 'eq',
      value: form.conditionValue.trim(),
      metadata: {},
    }, ...form.preservedConditions.map(editableCondition)];
  }
  if (form.discountType === 'code_required') return form.preservedConditions.map(editableCondition);
  return [];
}

function buildEligibilityScopes(form: DiscountFormState): PromotionEligibilityScope[] {
  return form.eligibilityScopes.map((scope, index) => {
    if (!scope.scopeType.trim()) throw new Error(`Eligibility scope ${index + 1} type is required.`);
    if (!scope.scopeValue.trim()) throw new Error(`Eligibility scope ${index + 1} value is required.`);
    return {
      scope_type: scope.scopeType,
      scope_value: scope.scopeValue.trim(),
      exclude: scope.exclude,
      metadata: scope.metadata,
    };
  });
}

function buildCodes(form: DiscountFormState): PromotionCode[] | undefined {
  const code = form.code.trim();
  if (!code) return undefined;
  return [{
    code,
    status: form.codeStatus,
    use_limit: parseOptionalInteger(form.codeUseLimit, 'Code use limit') ?? null,
    starts_at: toISOString(form.codeStartsAt, 'Code start time') || null,
    ends_at: toISOString(form.codeEndsAt, 'Code end time') || null,
    metadata: {},
  }];
}

function buildAutoPayConfiguration(form: DiscountFormState): PromotionAutoPayConfiguration | null {
  const hasAutoPayCondition = form.discountType === 'conditional'
    || form.preservedConditions.some(condition => condition.condition_type === 'autopay_enabled');
  if (!hasAutoPayCondition) return null;
  validateAutoPayConfiguration(form.autoPayConfiguration);
  const draft = form.autoPayConfiguration;
  return {
    label: draft.label.trim(),
    description: draft.description.trim(),
    required: draft.required,
    true_label: draft.trueLabel.trim(),
    false_label: draft.falseLabel.trim(),
    disclosure: draft.disclosure.trim(),
    enrollment_status: 'pending',
    deferred_enrollment_copy: draft.deferredEnrollmentCopy.trim(),
  };
}

export function buildPromotionPayload(
  form: DiscountFormState,
  includeCodes: boolean,
): Record<string, unknown> {
  if (form.discountType === 'code_required' && !['customer_code', 'hidden_code'].includes(form.redemptionMethod)) {
    throw new Error('Code-required discounts must use code redemption.');
  }
  const codes = includeCodes ? buildCodes(form) : undefined;
  if (form.discountType === 'code_required' && includeCodes && !codes?.length) {
    throw new Error('A redemption code is required for code-required discounts.');
  }

  const payload: Record<string, unknown> = {
    promotion_key: form.promotionKey.trim(),
    name: form.name.trim(),
    description: form.description.trim() || null,
    status: form.status,
    discount_type: form.discountType,
    redemption_method: form.redemptionMethod,
    stack_policy: form.stackPolicy,
    priority: parseOptionalInteger(form.priority, 'Priority') ?? 100,
    display: parseJSONObject(form.displayJSON, 'Display'),
    metadata: parseJSONObject(form.metadataJSON, 'Metadata'),
    conditions: buildConditions(form),
    eligibility_scopes: buildEligibilityScopes(form),
    autopay_configuration: buildAutoPayConfiguration(form),
    benefits: [buildBenefit(form)],
  };
  const startsAt = toISOString(form.startsAt, 'Start time');
  const endsAt = toISOString(form.endsAt, 'End time');
  payload.starts_at = startsAt ?? null;
  payload.ends_at = endsAt ?? null;
  payload.stack_group = form.stackGroup.trim() || null;
  if (codes) payload.codes = codes;
  return payload;
}
